import React from "react";

import { Avatar, Box, Button, CircularProgress, IconButton } from "@mui/material";
import { Menu, MenuItem } from "@mui/material";
import { TextField } from "@mui/material";
import Popover from '@mui/material/Popover';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';


import { ThemeProvider, createTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

import { useMovie } from "../movie/MovieProvider";
import { useMovieSetter } from "../movie/MovieProvider";
import { useAlert } from './AlertContext';

import VEJSONEditorDialog from './VEJSONEditorDialog';
import APIHelper from "../libs/api.js";
import J2VAuth from "../libs/auth.js";

export default function VETopBar(props) {

    const movie = useMovie();
    const setMovie = useMovieSetter();
    const { showAlert } = useAlert();

    const theme = createTheme({
        components: {
            MuiTextField: {
                defaultProps: {
                    variant: 'standard',
                },
            },
        },
        palette: {
            primary: {
                main: '#FFF',
            },
            secondary: {
                main: 'rgb(2 132 199)',
            },
        }
    });

    const templateNameStyles = makeStyles({
        root: {
            '& .MuiInputBase-input': {
                color: 'white', // Set text color to white
            },
            '& .MuiOutlinedInput-root': {
                '& fieldset': {
                    borderColor: 'transparent', // Set border color to gray
                },
                '&:hover fieldset': {
                    borderColor: 'gray', // Set border color to gray on hover
                },
                '&.Mui-focused fieldset': {
                    borderColor: 'gray', // Set border color to gray when focused
                },
            },
        },
    });

    const loginButtonStyles = makeStyles({
        root: {
            '& .MuiInputBase-input': {
                color: 'black', // Set text color to white
            },
            '& .MuiOutlinedInput-root': {
                '& fieldset': {
                    borderColor: 'gray', // Set border color to gray
                },
                '&:hover fieldset': {
                    borderColor: 'gray', // Set border color to gray on hover
                },
                '&.Mui-focused fieldset': {
                    borderColor: 'gray', // Set border color to gray when focused
                },
            },
        },
    });

    const templateNameClasses = templateNameStyles();
    const loginButtonClasses = loginButtonStyles();

    const [openJSONEditor, setOpenJSONEditor] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);

    const [avatarEl, setAvatarEl] = React.useState(null);
    const [loginEl, setLoginEl] = React.useState(null);

    const [loginAPIKey, setLoginAPIKey] = React.useState("");

    const [templateName, setTemplateName] = React.useState("Untitled");

    const userCredentials = J2VAuth.getCredentials();

    React.useEffect(() => {
        if (props.loadedTemplate && props.loadedTemplate.name) {
            setTemplateName(props.loadedTemplate.name);
        }
        else {
            console.log("No template name. Setting to 'Untitled'", props.loadedTemplate);
            setTemplateName("Untitled");
        }
    }, [props.loadedTemplate]);

    return (
        <>

            <Box className="flex justify-between items-center h-14 pl-2 pr-6 bg-gray-800">
                <Box className="text-white text-2xl flex items-center">
                    <IconButton sx={{ color: "#DDD" }} onClick={goBack}>
                        <ArrowBackIcon />
                    </IconButton>
                    <img src="https://json2video.com/images/json2video-logo.svg" alt="logo" className="h-8 m-2" />
                    <span>
                        JSON2Video
                    </span>
                </Box>
                <ThemeProvider theme={theme}>
                    {showTemplateMenu()}
                </ThemeProvider>
                {userProfile()}
            </Box>
            {
                openJSONEditor &&
                <VEJSONEditorDialog json={props.movieJSON} openDialog={openJSONEditor} setOpenDialog={setOpenJSONEditor} updateHandler={updateMovieJSON} />
            }
        </>
    );

    function userProfile() {

        console.log("userProfile: ", userCredentials);

        const userProfile = J2VAuth.getUserProfile();
        if (!userProfile) {
            return (
                <Box>
                    <ThemeProvider theme={theme}>
                        <Button color="primary" sx={{ fontSize: '10px' }} onClick={(event) => setLoginEl(event.currentTarget)}>Log in</Button>
                    </ThemeProvider>
                    <Popover id="login-menu" anchorEl={loginEl} open={!!loginEl} onClose={() => setLoginEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                        <Box className="w-80 p-4 flex flex-col">
                            <Box className="mt-1">
                                <TextField label="Enter your API Key" className={loginButtonClasses.root + "pt-5 w-full"} size="small" variant="outlined" value={loginAPIKey} onChange={(event) => setLoginAPIKey(event.target.value)} autoComplete="off" />
                            </Box>
                            <Box className="mt-3">
                                <Button className="w-full" color="primary" variant="outlined" size="small" onClick={loginUserIn}>Login</Button>
                            </Box>
                        </Box>
                    </Popover>
                </Box>
            );
        }

        const userProfileImage = userProfile.picture ?? null;

        return (
            <Box>
                <Avatar
                    sx={{ fontSize: "15px", m: 1 }}
                    src={userProfileImage}
                    className="bg-red-400 cursor-pointer"
                    onClick={(event) => setAvatarEl(event.currentTarget)}
                />
                <Menu id="avatar-menu" anchorEl={avatarEl} open={!!(avatarEl)} onClose={() => setAvatarEl(null)}>
                    <MenuItem onClick={() => { setAvatarEl(null); J2VAuth.logout(); window.location.href = "/"; }}>
                        <LogoutIcon className="mr-2" /> Logout
                    </MenuItem>
                </Menu>
            </Box>
        );

        async function loginUserIn() {
            console.log("loginUserIn: ", loginAPIKey);

            // validate the API key length
            if (loginAPIKey.length !== 40) {
                showAlert({
                    title: "Invalid API Key",
                    message: "Please enter a valid API Key"
                });
                return;
            }
            
            // hide the login pop-up
            setLoginEl(null);

            // try to login
            const result = await J2VAuth.login(null, loginAPIKey);
            if (result && result.success) {
                showAlert({
                    title: "Login Successful",
                    message: "You are now logged in"
                });
                props.setUserCredentials(J2VAuth.getCredentials());
            }
            else {
                showAlert({
                    title: "Invalid API Key",
                    message: "Please enter a valid API Key"
                });
                return;
            }
        }
    }

    function updateMovieJSON(newJSON) {
        setMovie({
            type: "updateMovie",
            movie: newJSON
        });
    }

    function newEmptyMovie() {
        // console.log("User loaded a new empty movie");

        setAnchorEl(null);

        props.setLoadedTemplate(null);
        setMovie({
            type: "updateMovie",
            movie: props.defaultMovie
        });
    }

    //
    // Template menu
    //
    function showTemplateMenu() {

        return (
            <Box className="flex">
                <Button
                    color="primary" sx={{ fontSize: '8px' }}
                    size="small" endIcon={<KeyboardArrowDownIcon />}
                    onClick={(event) => setAnchorEl(event.currentTarget)}>Template</Button>
                <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={!!anchorEl}
                    onClose={() => setAnchorEl(null)}
                    MenuListProps={{
                        'aria-labelledby': 'basic-button',
                    }}
                >
                    <MenuItem disableRipple onClick={newEmptyMovie}>
                        <FiberNewIcon className="mr-3" />
                        New empty template...
                    </MenuItem>
                    <MenuItem disableRipple disabled={!userCredentials} onClick={saveTemplateAs} >
                        <FileCopyIcon className="mr-3" />
                        Save template as...
                    </MenuItem>
                    <MenuItem disableRipple onClick={() => setOpenJSONEditor(true)}>
                        <ImportExportIcon className="mr-3" />
                        Edit template's JSON
                    </MenuItem>
                </Menu>
                {
                    showTemplateName()
                }
                {
                    showSavingStatus()
                }
            </Box>
        );
    }

    function showTemplateName() {

        if (props.savingStatus === "preview") return;

        return (
            <TextField
                size="small"
                className={templateNameClasses.root}
                variant="outlined"
                value={templateName}
                disabled={!(props.loadedTemplate)}
                onChange={(event) => setTemplateName(event.target.value)}
                onBlur={saveTemplateName}
                sx={{ color: "white", fontSize: "12px", m: 1 }}
            />
        );
    }

    function showSavingStatus() {

        switch (props.savingStatus) {
            case "not_saved": {
                return (
                    <Box className="w-6 ml-3 flex items-center justify-center">
                        <CloudUploadIcon fontSize="small" className="text-yellow-400" />
                    </Box>
                );
            }
            case "saving": {
                return (
                    <Box className="w-6 ml-3 flex items-center justify-center">
                        <CircularProgress size={15} />
                    </Box>
                );
            }
            case "saved": {
                return (
                    <Box className="w-6 ml-3 flex items-center justify-center">
                        <CloudDoneIcon fontSize="small" className="text-gray-600" />
                    </Box>
                );
            }
            case "preview": {
                return (
                    <Box className="w-32 ml-3 flex items-center justify-center">
                        <Box className="bg-gray-600 rounded-sm p-1 text-gray-900 text-xs">PREVIEW MODE</Box>
                    </Box>
                )
            }
            default: {
                return (
                    <Box className="w-6 ml-3 flex items-center justify-center">
                        <CloudOffIcon fontSize="small" className="text-red-600" />
                    </Box>
                );
            }
        }
    }

    function saveTemplateName() {
        if (props.loadedTemplate && templateName !== props.loadedTemplate?.name) {
            props.setLoadedTemplate({ ...props.loadedTemplate, name: templateName });
        }
    }

    async function saveTemplateAs() {

        setAnchorEl(null);

        showAlert({
            title: "Saving...",
            message: "Please wait...",
            actions: []
        });

        if (!userCredentials) {
            showAlert({
                title: "Oops! You are not logged in!",
                message: [
                    "<strong>You need an account and an API key to save a template</strong>.",
                    "Please, login or sign up to JSON2Video to save a template."
                ],
                actions: [{
                    title: "Close",
                    variant: "contained"
                }, {
                    title: "Login/Sign Up",
                    variant: "outlined",
                    handler: () => {
                        window.open("/login?redirect=/tools/visual-editor/", "_blank").focus();
                    }
                }]
            });
            return;
        }

        props.setSavingStatus("saving");

        const newTemplateName = (props.loadedTemplate?.name ?? "Untitled") + " (copy)";
        const newTemplateJSON = JSON.stringify(movie.movieObject);
        const newTemplate = {
            name: newTemplateName,
            movie: newTemplateJSON
        };

        console.log("Saving template as: ", newTemplate);

        const apiCall = await APIHelper.saveTemplate(null, newTemplate);
        if (apiCall) {
            if (apiCall.success) {
                console.log("saveTemplateAs(): ", apiCall);
                props.setLoadedTemplate({
                    id: apiCall.templateId, // the new template ID
                    name: newTemplateName,
                    movie: newTemplateJSON
                });
                setTemplateName(newTemplateName);
                // change URL to the new template
                window.history.replaceState(null, "", `/tools/visual-editor/?template=${apiCall.templateId}`);
                props.setSavingStatus("saved");
                props.loadTemplate(apiCall.templateId);
                showAlert({
                    title: "Template saved",
                    message: ["Template saved successfully!"]
                })
                return;
            }
        }

        // If error, show a message
        props.setSavingStatus("error");
        showAlert({
            title: "Error saving template",
            message: [
                apiCall?.message ?? "There was an error saving your template, but it's not clear what happened.",
                "Please, try again later."
            ]
        });
    }

    function goBack() {
        if (!userCredentials) {
            // go to homepage
            window.location.href = "/";
        }
        else {
            // go to dashboard
            window.location.href = "/dashboard/templates"
        }
    }
}